import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { AtributosComprasForm, AtributosComprasSchema } from "../schemas/AtributosCompras_cadastro/atributos_compras";
import { TelasIds } from "../../../../common/utils/tela_ids";

/**
 * useAtributosComprasCadastroViewModel
 */

export const useAtributosComprasCadastroViewModel = () => {
  const {
    commons: {
      getAtributosCompraUseCase,
    },
    compras: {
      updateAtributosComprasUseCase,
    },
  } = useDependencyInjection();

  const { pushInfoNotification, pushErrorNotification, setPageSaving } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const hookForm = useForm<AtributosComprasForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(AtributosComprasSchema),
  });

  /**
   * Funcoes de carregamento
   */

  const loadAtributoCompra = useCallback(async () => {
    try {
      const atributoCompra = await getAtributosCompraUseCase.getAtributosCompra(
        TelasIds.AtributosCompras_cadastro,
      );

      if (atributoCompra === null) {
        throw new Error('Falha ao buscar atributos de compras!');
      }
      hookForm.setValue('exibirDataCadastro', atributoCompra.exibirDataCadastro === true ? 1 : 0);
      hookForm.setValue('exibirDataCadastroRequisicao', atributoCompra.exibirDataRequisicao === true ? 1 : 0);
      hookForm.setValue('exibirDataAprovacaoColetaPreco', atributoCompra.exibirDataAprovacaoColetaPreco === true ? 1 : 0);
      hookForm.setValue('notaCorte', atributoCompra.notaCorteFornecedor ?? 0);
      hookForm.setValue('observacoes', atributoCompra.observacaoGeral ?? '');
    } catch (e: unknown) {
      throw new Error('Falha ao buscar atributos de compras!');
    }
  }, [
    hookForm,
    getAtributosCompraUseCase
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadAtributoCompra();
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadAtributoCompra();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadAtributoCompra,
    pushErrorNotification,
  ]);

  const handleAtributosComprasSubmit = useCallback(async (form: AtributosComprasForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      await updateAtributosComprasUseCase.updateAtributosCompras(
        form.exibirDataCadastro,
        form.exibirDataCadastroRequisicao,
        form.exibirDataAprovacaoColetaPreco,
        form.notaCorte,
        form.observacoes,
      );

      await loadAtributoCompra();

      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    updateAtributosComprasUseCase,
    loadAtributoCompra,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    hookForm,
    handleReload,
    handleSubmit: hookForm.handleSubmit(handleAtributosComprasSubmit),
  };
}